/**
 * Authentication middleware for API client
 *
 * Automatically injects Firebase Auth ID tokens into all API requests.
 * This eliminates the need to manually pass Authorization headers in every API call.
 */

import { getAuth } from "firebase/auth";
import { createLogger } from "../utils/logger";

const logger = createLogger("auth-middleware");

// Tile requests are public and deliberately anonymous, so they stay
// edge-cacheable: Cloudflare treats a request bearing `Authorization`
// specially, which would undermine the caching the tile endpoint exists
// for, and the header also turns an otherwise-simple CORS GET into one that
// needs a preflight OPTIONS. Do not "fix" this back -- only tile requests
// are exempted, every other endpoint keeps today's behaviour.
const isTileRequestUrl = (url: string): boolean =>
  url.includes("/search/campgrounds/tile/");

/**
 * Request interceptor that adds Authorization header with Firebase ID token
 */
export const authMiddleware = async (request: Request): Promise<Request> => {
  if (isTileRequestUrl(request.url)) {
    return request;
  }

  try {
    // Get current Firebase auth user
    const auth = getAuth();
    const user = auth.currentUser;

    // If user is logged in, get their ID token and add to headers
    if (user) {
      const idToken = await user.getIdToken();
      request.headers.set("Authorization", `Bearer ${idToken}`);
    }
  } catch (error) {
    // Log error but don't block the request
    // Some requests may not require authentication
    logger.warn("Failed to add auth token to request:", error);
  }

  return request;
};
